import { CommonModule, NgClass } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { finalize, Subject, takeUntil } from 'rxjs';

import {
  CryptoService,
  LimitCryptoStatus,
  LimitStatusModalState,
} from '../../../core/services/crypto.service';

@Component({
  selector: 'app-limit-status-modal',
  standalone: true,
  imports: [CommonModule, NgClass],
  templateUrl: './limit-status-modal.component.html',
  styleUrls: ['./limit-status-modal.component.scss'],
})
export class LimitStatusModalComponent implements OnInit, OnDestroy {
  @ViewChild('limitCameraVideo') limitCameraVideoRef?: ElementRef<HTMLVideoElement>;
  @ViewChild('limitCameraCanvas') limitCameraCanvasRef?: ElementRef<HTMLCanvasElement>;

  modalState: LimitStatusModalState = {
    visible: false,
    status: '',
    title: '',
    description: '',
    requiresDocuments: false,
  };

  documentSource: 'CAMERA' | 'UPLOAD' | null = null;
  documentFrontFile?: File;
  documentBackFile?: File;
  documentSubmitError = '';
  documentSubmitSuccess = '';
  isSubmittingDocuments = false;
  cameraStream: MediaStream | null = null;
  isCameraActive = false;
  frontPreviewUrl = '';
  backPreviewUrl = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly cryptoService: CryptoService,
  ) {}

  ngOnInit(): void {
    this.cryptoService.limitStatusModal$
      .pipe(takeUntil(this.destroy$))
      .subscribe((state) => {
        const wasVisible = this.modalState.visible;
        this.modalState = state;

        if (!state.visible && wasVisible) {
          this.stopCamera();
          this.clearDocumentFiles();
          this.documentSource = null;
          this.documentSubmitError = '';
          this.documentSubmitSuccess = '';
        }
      });
  }

  close(): void {
    if (!this.canCloseModal()) {
      return;
    }

    this.stopCamera();
    this.cryptoService.dismissLimitStatusModal();
  }

  canCloseModal(): boolean {
    return this.modalState.status !== 'CANCELED';
  }

  selectDocumentSource(source: 'CAMERA' | 'UPLOAD'): void {
    this.documentSource = source;
    this.documentSubmitError = '';
    this.documentSubmitSuccess = '';
    this.clearDocumentFiles();

    if (source === 'CAMERA') {
      this.startCamera();
      return;
    }

    this.stopCamera();
  }

  onDocumentFileSelected(event: Event, type: 'front' | 'back'): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];

    this.documentSubmitError = '';
    this.documentSubmitSuccess = '';

    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      this.documentSubmitError = 'Formato inválido. Envie imagem JPG, PNG ou WEBP.';
      input.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.documentSubmitError = 'Arquivo muito grande. O limite é 10MB por imagem.';
      input.value = '';
      return;
    }

    if (type === 'front') {
      this.documentFrontFile = file;
      return;
    }

    this.documentBackFile = file;
  }

  submitLimitDocuments(): void {
    this.documentSubmitError = '';
    this.documentSubmitSuccess = '';

    if (!this.documentSource) {
      this.documentSubmitError = 'Selecione se deseja tirar foto ou enviar fotos.';
      return;
    }

    if (!this.documentFrontFile || !this.documentBackFile) {
      this.documentSubmitError = 'Envie frente e verso do documento para continuar.';
      return;
    }

    const formData = new FormData();

    formData.append('documentFront', this.documentFrontFile);
    formData.append('documentBack', this.documentBackFile);

    this.isSubmittingDocuments = true;

    this.cryptoService
      .sendDocuments(formData)
      .pipe(
        finalize(() => {
          this.isSubmittingDocuments = false;
        }),
      )
      .subscribe({
        next: () => {
          this.documentSubmitSuccess = 'Documentos enviados com sucesso. Sua análise foi reenviada.';
          this.stopCamera();
          this.clearDocumentFiles();
          this.documentSource = null;
          this.cryptoService.setLimitStatusModalByStatus('PENDING_REVIEW');
        },
        error: () => {
          this.documentSubmitError = 'Não foi possível enviar os documentos agora. Tente novamente em instantes.';
        },
      });
  }

  captureFromCamera(type: 'front' | 'back'): void {
    const video = this.limitCameraVideoRef?.nativeElement;
    const canvas = this.limitCameraCanvasRef?.nativeElement;

    if (!video || !canvas || !this.isCameraActive) {
      this.documentSubmitError = 'A câmera ainda não está pronta. Aguarde alguns segundos.';
      return;
    }

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      this.documentSubmitError = 'Não foi possível capturar a imagem da câmera.';
      return;
    }

    ctx.drawImage(video, 0, 0, width, height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.documentSubmitError = 'Não foi possível gerar a foto capturada.';
          return;
        }

        const file = new File([blob], `${type}-document-${Date.now()}.jpg`, {
          type: 'image/jpeg',
        });

        this.documentSubmitError = '';
        this.documentSubmitSuccess = '';

        if (type === 'front') {
          if (this.frontPreviewUrl) {
            URL.revokeObjectURL(this.frontPreviewUrl);
          }
          this.documentFrontFile = file;
          this.frontPreviewUrl = URL.createObjectURL(file);
          return;
        }

        if (this.backPreviewUrl) {
          URL.revokeObjectURL(this.backPreviewUrl);
        }
        this.documentBackFile = file;
        this.backPreviewUrl = URL.createObjectURL(file);
      },
      'image/jpeg',
      0.94,
    );
  }

  getStatusClass(status?: string): string {
    const value = (status || '').toUpperCase();

    if (['PAID', 'APPROVED', 'COMPLETED', 'CONFIRMED', 'SUCCESS'].includes(value)) {
      return 'status-success';
    }

    if (['PENDING', 'PROCESSING', 'WAITING', 'PENDING_DOCUMENTS', 'PENDING_REVIEW'].includes(value)) {
      return 'status-pending';
    }

    if (['FAILED', 'CANCELED', 'CANCELLED', 'REJECTED', 'ERROR'].includes(value)) {
      return 'status-error';
    }

    return 'status-default';
  }

  getLimitStatusDisplay(status?: LimitCryptoStatus | ''): string {
    const value = String(status || '').toUpperCase();

    if (value === 'PENDING') {
      return 'Pendente';
    }
    if (value === 'APPROVED') {
      return 'Aprovado';
    }
    if (value === 'REJECTED') {
      return 'Rejeitado';
    }
    if (value === 'CANCELED') {
      return 'Cancelado';
    }
    if (value === 'PENDING_DOCUMENTS') {
      return 'Documentos pendentes';
    }
    if (value === 'PENDING_REVIEW') {
      return 'Em analise';
    }

    return status || '-';
  }

  private startCamera(attempt = 0): void {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      this.documentSubmitError = 'Seu dispositivo/navegador não suporta câmera nesta tela. Use Enviar fotos.';
      return;
    }

    const video = this.limitCameraVideoRef?.nativeElement;

    if (!video) {
      if (attempt < 8) {
        setTimeout(() => this.startCamera(attempt + 1), 80);
      }
      return;
    }

    this.stopCamera();

    navigator.mediaDevices
      .getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      })
      .then((stream) => {
        this.cameraStream = stream;
        video.srcObject = stream;
        video
          .play()
          .then(() => {
            this.isCameraActive = true;
          })
          .catch(() => {
            this.documentSubmitError = 'Não foi possível iniciar o preview da câmera. Use Enviar fotos.';
          });
      })
      .catch(() => {
        this.documentSubmitError = 'Não foi possível acessar a câmera. Verifique permissões do navegador.';
      });
  }

  private stopCamera(): void {
    if (this.cameraStream) {
      this.cameraStream.getTracks().forEach((track) => track.stop());
      this.cameraStream = null;
    }

    if (this.limitCameraVideoRef?.nativeElement) {
      this.limitCameraVideoRef.nativeElement.srcObject = null;
    }

    this.isCameraActive = false;
  }

  private clearDocumentFiles(): void {
    this.documentFrontFile = undefined;
    this.documentBackFile = undefined;

    if (this.frontPreviewUrl) {
      URL.revokeObjectURL(this.frontPreviewUrl);
      this.frontPreviewUrl = '';
    }

    if (this.backPreviewUrl) {
      URL.revokeObjectURL(this.backPreviewUrl);
      this.backPreviewUrl = '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopCamera();
    this.clearDocumentFiles();
  }
}
